import { useState, useEffect } from 'react';
import { supabase, handleSupabaseError, retryOperation } from '../lib/supabase';
import { useAuth } from './useAuth';

interface GardenerProfile {
  id: string;
  level: 'brand_new' | 'beginner' | 'intermediate' | 'expert';
  grow_vegetables: boolean;
  grow_flowers: boolean;
  grow_fruits: boolean;
  grow_herbs: boolean;
  garden_type: ('raised_beds' | 'in_ground' | 'containers')[];
  zip_code: string | null;
  onboarding_completed: boolean;
  first_name: string | null;
  last_name: string | null;
  first_frost_date: string | null;
  last_frost_date: string | null;
  is_admin: boolean;
  // New fields for onboarding
  growing_space_type: 'raised_bed' | 'in_ground' | 'containers' | 'none_yet' | null;
  sunlight_hours: 'full_sun' | 'full_to_part_sun' | 'part_sun_part_shade' | 'full_shade' | null;
  has_seeds: boolean | null;
  has_soil_compost: boolean | null;
  has_fertilizer: boolean | null;
  has_containers_bed: boolean | null;
  primary_goal: 'fresh_salads_fast' | 'fun_with_kids' | 'lower_grocery_bill' | 'stress_relief' | 'learn_basics' | null;
  phone_number: string | null;
}

export function useGardenerProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<GardenerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    let retryCount = 0;
    const maxRetries = 3;

    async function fetchProfile() {
      if (!user) {
        if (mounted) {
          setProfile(null);
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await retryOperation(() =>
          supabase
            .from('gardener_profiles')
            .select('*')
            .eq('id', user.id)
            .maybeSingle()
        );

        if (fetchError) {
          // Only treat it as an error if it's not a "no rows returned" error
          if (fetchError.code !== 'PGRST116') {
            throw fetchError;
          }
        }
        
        if (mounted) {
          setProfile(data);
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching gardener profile:', err);
        if (mounted) {
          if (retryCount < maxRetries) {
            retryCount++;
            // Exponential backoff
            const delay = Math.pow(2, retryCount) * 1000;
            setTimeout(fetchProfile, delay);
          } else {
            const errorMessage = handleSupabaseError(err);
            setError(errorMessage);
            setProfile(null);
          }
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    // Add a small delay to ensure auth is fully initialized
    const timer = setTimeout(fetchProfile, 500);
    
    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [user]); // Only re-run when user changes

  return { profile, loading, error };
}